<template>
    <div class="payable-receivable-partial-payment-container">
        <Loading v-if="loading" />

        <template v-if="selectedId">

            <div v-show="!loading">
                <Alert type="info">
                    <span>Saldo disponível para baixa: <b>{{ saldo | currency }}</b></span>
                </Alert>

                <div class="payable-receivable-partial-payment-registered-data" v-if="hasPersistedCategory">
                    <div class="payable-receivable-partial-payment-registered-item">
                        <span class="payable-receivable-partial-payment-registered-label">Categoria</span>
                        <span class="payable-receivable-partial-payment-registered-value">
                            {{ record.chartOfAccounts.content || "-" }}
                        </span>
                    </div>
                </div>
                <PayableReceivablePartialPaymentHistory v-show="!loading" :payableReceivableId="selectedId"
                    @hide-actions="onHistoryHideActions" @loaded="onHistoryLoaded" />

                <template v-if="!hasPersistedCategory">
                    <Select title="Categoria" field="category" :formName="formName" :required="true"
                        placeholder="Selecione a categoria" v-model="payableReceivablePartialPayment.chartOfAccounts"
                        url="api/v1/finance/chart-of-accounts/select-all" nameNewRegister="chartOfAccountsCreateUpdate"
                        titleNewRegister="Categoria" :widthNewRegister="500" :heightNewRegister="250">
                        <ChartOfAccountsCreateUpdate :registerInSelect="true"
                            v-model="payableReceivablePartialPayment.chartOfAccounts" />
                    </Select>
                </template>

                <template v-if="!historyEditing">
                    <b-row>
                        <b-col xs="12" sm="12" md="6">
                            <Select title="Conta Bancária" field="bankAccount" :formName="formName" :required="true"
                                placeholder="Selecione a conta bancária"
                                v-model="payableReceivablePartialPayment.bankAccount"
                                url="api/v1/finance/bank-account/select-all" :showNewRegister="false">
                            </Select>
                        </b-col>
                        <b-col xs="12" sm="12" md="6">
                            <Select title="Tipo de Pagamento" field="typePayment" :formName="formName" :required="true"
                                placeholder="Selecione o tipo de pagamento"
                                v-model="payableReceivablePartialPayment.typePayment"
                                url="/api/v1/finance/type-payment/select-all" nameNewRegister="typePaymentCreateUpdate"
                                titleNewRegister="Tipo de Pagamento" :widthNewRegister="500" :heightNewRegister="250">
                                <TypePaymentCreateUpdate :registerInSelect="true"
                                    v-model="payableReceivablePartialPayment.typePayment" />
                            </Select>
                        </b-col>
                    </b-row>

                    <b-row>
                        <b-col xs="12" sm="12" md="6">
                            <InputDecimal title="Valor da Baixa" field="value" :formName="formName" :required="true"
                                :disabled="!canPartialPay" v-model="payableReceivablePartialPayment.value" />
                            <span class="payable-receivable-partial-payment-error" v-if="exceededMessage">{{
                                exceededMessage
                                }}</span>
                        </b-col>
                        <b-col xs="12" sm="12" md="6">
                            <DateTime v-model="payableReceivablePartialPayment.conciliationDate"
                                :title="'Data de Conciliação'" field="conciliationDate" :formName="formName"
                                :format="'DD/MM/YYYY'" :disabled="!canPartialPay"
                                :placeholder="'Selecione a data de conciliação'" />
                        </b-col>
                    </b-row>
                </template>
            </div>
        </template>
    </div>
</template>

<script>
import Alert from "@/ui/layout/Alert";
import DateTime from "@/ui/forms/DateTime";
import InputDecimal from "@/ui/forms/InputDecimal";
import Loading from "@/ui/shared/Loading.vue";
import Select from "@/ui/forms/Select";
import TypePaymentCreateUpdate from "@/modules/finance/components/typePayment/TypePaymentCreateUpdate.vue";
import ChartOfAccountsCreateUpdate from "@/modules/finance/components/chartOfAccounts/ChartOfAccountsCreateUpdate.vue";
import PayableReceivablePartialPaymentHistory from "./PayableReceivablePartialPaymentHistory.vue";

import { mapState, mapMutations } from "vuex";

export default {
    name: "PayableReceivablePartialPayment",
    components: {
        Alert,
        DateTime,
        InputDecimal,
        Loading,
        Select,
        TypePaymentCreateUpdate,
        ChartOfAccountsCreateUpdate,
        PayableReceivablePartialPaymentHistory,
    },
    data() {
        return {
            formName: "payableReceivablePartialPaymentForm",
            loading: true,
            saldo: 0,
            exceededMessage: "",
            historyEditing: false,
            record: {
                chartOfAccounts: { id: "", content: "" },
                typePayment: { id: "", content: "" },
                bankAccount: { id: "", content: "" },
            },
            payableReceivablePartialPayment: {
                value: 0,
                conciliationDate: this.getTodayBRDate(),
                chartOfAccounts: { id: "", content: "" },
                typePayment: { id: "", content: "" },
                bankAccount: { id: "", content: "" },
            },
        };
    },
    mounted() {
        this.$emit("valid", false);
    },
    computed: {
        ...mapState("generic", ["selected"]),
        selectedId() {
            return this.selected[0];
        },
        canPartialPay() {
            return !!this.payableReceivablePartialPayment.chartOfAccounts.id;
        },
        hasPersistedCategory() {
            return !!this.record.chartOfAccounts.id;
        },
    },
    methods: {
        ...mapMutations("validation", ["addValidation", "removeValidation"]),
        getTodayBRDate() {
            const today = new Date();
            const day = String(today.getDate()).padStart(2, "0");
            const month = String(today.getMonth() + 1).padStart(2, "0");
            const year = today.getFullYear();
            return `${day}/${month}/${year}`;
        },
        resetRecordState() {
            this.saldo = 0;
            this.exceededMessage = "";
            this.record = {
                chartOfAccounts: { id: "", content: "" },
                typePayment: { id: "", content: "" },
                bankAccount: { id: "", content: "" },
            };
            this.payableReceivablePartialPayment = {
                value: 0,
                conciliationDate: this.getTodayBRDate(),
                chartOfAccounts: { id: "", content: "" },
                typePayment: { id: "", content: "" },
                bankAccount: { id: "", content: "" },
            };
        },
        onHistoryLoaded(content) {
            const payableReceivable = content.payableReceivable || {};

            this.record.chartOfAccounts = payableReceivable.chartOfAccounts?.id
                ? { id: payableReceivable.chartOfAccounts.id, content: payableReceivable.chartOfAccounts.content }
                : { id: "", content: "" };
            this.record.typePayment = payableReceivable.typePayment?.id
                ? { id: payableReceivable.typePayment.id, content: payableReceivable.typePayment.content }
                : { id: "", content: "" };
            this.record.bankAccount = payableReceivable.bankAccount?.id
                ? { id: payableReceivable.bankAccount.id, content: payableReceivable.bankAccount.content }
                : { id: "", content: "" };

            this.saldo = content.remainingValue ?? 0;

            this.payableReceivablePartialPayment.chartOfAccounts = this.record.chartOfAccounts;
            this.payableReceivablePartialPayment.typePayment = this.record.typePayment;
            this.payableReceivablePartialPayment.bankAccount = this.record.bankAccount;

            this.validateRequiredFields();
            this.loading = false;
            this.emitValid();
        },
        onHistoryHideActions(value) {
            this.historyEditing = !!value;
            this.$emit("hide-actions", this.historyEditing);
            this.emitValid();
        },
        validateRequiredFields() {
            const key = "requiredFields&" + this.formName;
            this.removeValidation({ key, formName: this.formName });

            if (!this.canPartialPay) {
                this.addValidation({
                    key,
                    formName: this.formName,
                    notification: "Selecione ou cadastre uma Categoria para continuar.",
                });
            }
        },
        formatCurrency(value) {
            return Number(value || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
        },
        validateValue(rawValue) {
            const key = "value&" + this.formName;
            this.removeValidation({ key, formName: this.formName });
            this.exceededMessage = "";

            if (rawValue > this.saldo) {
                this.exceededMessage = `O valor da baixa não pode ser maior que o saldo disponível (${this.formatCurrency(this.saldo)}).`;
                this.addValidation({
                    key,
                    formName: this.formName,
                    notification: this.exceededMessage,
                });
            }

            this.emitValid();
        },
        emitValid() {
            if (this.historyEditing) {
                this.$emit("valid", false);
                return;
            }

            const value = this.payableReceivablePartialPayment.value;
            const hasBankAccount = !!this.payableReceivablePartialPayment.bankAccount?.id;
            const hasTypePayment = !!this.payableReceivablePartialPayment.typePayment?.id;
            const valid = this.canPartialPay
                && hasBankAccount
                && hasTypePayment
                && value > 0
                && value <= this.saldo;
            this.$emit("valid", valid);
        },
    },
    watch: {
        payableReceivablePartialPayment: {
            handler(value) {
                this.$emit("input", {
                    payableReceivableId: this.selectedId,
                    value: value.value,
                    conciliationDate: value.conciliationDate || this.getTodayBRDate(),
                    typePayment: value.typePayment,
                    bankAccount: value.bankAccount,
                    chartOfAccounts: value.chartOfAccounts,
                });
                this.emitValid();
            },
            deep: true,
            immediate: true,
        },
        "payableReceivablePartialPayment.value"(newVal) {
            this.validateValue(newVal);
        },
        saldo() {
            this.validateValue(this.payableReceivablePartialPayment.value);
        },
        canPartialPay() {
            this.emitValid();
        },
        selectedId(newId, oldId) {
            if (newId && newId !== oldId) {
                this.resetRecordState();
                this.loading = true;
            }
        },
    },
};
</script>

<style scoped>
.payable-receivable-partial-payment-container {
    display: flex;
    flex-direction: column;
    gap: 8px;
    padding: 4px 0;
}

.payable-receivable-partial-payment-registered-data {
    display: flex;
    flex-direction: column;
    gap: 4px;
    background: var(--bg-elevated);
    border: 1px solid var(--border-subtle);
    border-radius: 8px;
    padding: 10px 12px;
    margin-bottom: 8px;
}

.payable-receivable-partial-payment-registered-item {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
}

.payable-receivable-partial-payment-registered-label {
    font-size: 13px;
    font-weight: 600;
    text-align: center;

}

.payable-receivable-partial-payment-registered-value {
    font-size: 13px;
    color: var(--text-primary);
    text-align: right;
}

.payable-receivable-partial-payment-error {
    display: block;
    color: var(--color-danger);
    font-size: 13px;
    margin-top: -4px;
}
</style>
